import React from 'react';
import './About.css';
import useIntersectionObserver from '../useIntersectionObserver'; // Ensure this path is correct

const heroBackgroundImage = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1900&q=80';
const empoweringImage = 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60';
const originImage = 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60';

const Section = ({ children, className = '', animationType = 'fade-in-up', id = '' }) => {
  const [elementRef, isVisible] = useIntersectionObserver({
    threshold: 0.1, // When 10% of the element is visible
    rootMargin: "0px 0px -50px 0px" // Trigger slightly before fully in view from bottom, adjust if needed
                                     // Try "0px 0px 100px 0px" to detect even earlier from bottom
  });

  // DEBUGGING LOG
  if (id === 'core-values' || className.includes('about-vision-mission-section')) {
    console.log(`SECTION [${id || className}] - isVisible: ${isVisible}, Element:`, elementRef.current);
  }

  return (
    <section
      id={id ? `about-${id}` : ''}
      ref={elementRef}
      className={`about-section ${className} ${isVisible ? 'is-visible' : ''} about-animate-${animationType}`}
    >
      {children}
    </section>
  );
};

const IconCard = ({ icon, title, children, animationDelay }) => {
    const [elementRef, isVisible] = useIntersectionObserver({
        threshold: 0.2 // When 20% of the card is visible
    });

    // DEBUGGING LOG
    console.log(`ICONCARD [${title}] - isVisible: ${isVisible}, Element:`, elementRef.current);

    return (
        <div
            ref={elementRef}
            className={`about-card-style about-icon-card ${isVisible ? 'is-visible' : ''}`}
            // Apply animation delay only when the IconCard itself becomes visible,
            // not just when the parent section is visible.
            style={{ transitionDelay: isVisible ? animationDelay : '0s' }}
        >
            <div className="about-card-icon">{icon}</div>
            <h3>{title}</h3>
            {children}
        </div>
    );
};

const AboutUs = () => {
  return (
    <div className="about-us-page">
      <header className="about-hero" style={{ backgroundImage: `url(${heroBackgroundImage})` }}>
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <h1 className="about-animate-pop-in">
            <span style={{color:"white"}}>About</span>
            <span className="about-highlighted-text">Adhvik & Adhrit</span>
          </h1>
          <p className="about-hero-tagline about-animate-fade-in-delay">
            Your Architect of Strategic Excellence & Human Capital Transformation
          </p>
          <div className="about-hero-scroll-indicator about-animate-fade-in-very-delay">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M12 19L7 14M12 19L17 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Scroll Down</span>
          </div>
        </div>
        <div className="about-hero-shape-divider">
            <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31.74,902.9,70.66,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="about-shape-fill"></path>
            </svg>
        </div>
      </header>

      <Section className="about-empowering-enterprises" animationType="slide-in-left-transform" id="empowering">
        <div className="about-section-header">
          <span className="about-section-subtitle">Our Foundation</span>
          <h2>Empowering Enterprises Through <span className="about-highlight">Expertise</span></h2>
        </div>
        <div className="about-section-content-wrapper">
          <div className="about-section-text">
            <p className="about-establishment-date">Established on <strong>20th September 2022</strong></p>
            <p>
              Adhvik & Adhrit has rapidly emerged as a trailblazing force in strategic
              consultancy and manpower solutions. With a laser focus on delivering excellence, we're
              synonymous with trust, integrity, and innovation in human capital management.
              We don't just provide services—<strong>we architect strategic impact.</strong>
            </p>
            <p>
              We specialize in connecting organizations with exceptional talent,
              designing sustainable workforce strategies, and delivering cutting-edge business
              consultancy. We empower businesses to thrive by helping
              them attract, retain, and develop the industry's best minds.
            </p>
          </div>
          <div className="about-section-visual about-interactive-visual">
            <div className="about-visual-bg-element"></div>
            <img src={empoweringImage} alt="Team collaborating on a project" />
            <div className="about-visual-overlay-text">Excellence Delivered</div>
          </div>
        </div>
      </Section>

      <Section className="about-origin-story" animationType="slide-in-right-transform" id="origin">
        <div className="about-section-header">
            <span className="about-section-subtitle">Our Journey</span>
            <h2>The <span className="about-highlight">Genesis</span> of Adhvik & Adhrit</h2>
        </div>
        <div className="about-section-content-wrapper about-reversed-layout">
            <div className="about-section-text">
                <p>
                Amidst evolving workforce demands and organizational shifts, our founders envisioned a
                firm to transform traditional staffing into <strong>purpose-driven partnerships.</strong> With
                diverse backgrounds in HR, consulting, and strategic operations, they launched Adhvik & Adhrit to
                solve real-world challenges with insight, precision, and heart.
                </p>
                <p>
                Our core philosophy is built on understanding the unique DNA of each client, enabling us to craft bespoke solutions that resonate and deliver lasting value.
                </p>
            </div>
            <div className="about-section-visual about-interactive-visual">
                <div className="about-visual-bg-element"></div>
                <img src={originImage} alt="Founders brainstorming ideas" />
                <div className="about-visual-overlay-text">Insight & Precision</div>
            </div>
        </div>
      </Section>

      <Section className="about-vision-mission-section" animationType="fade-in-up" id="core-values">
      {/* Changed animationType to simple fade-in-up for the section wrapper. Stagger will be on IconCards */}
        <div className="about-section-header">
            <span className="about-section-subtitle">Our Guiding Principles</span>
            <h2>Vision & <span className="about-highlight">Mission</span></h2>
        </div>
        <div className="about-vision-mission-grid">
          <IconCard
            animationDelay="0.1s" // Delay for the first card after section becomes visible
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
            }
            title="Our Vision"
          >
            <p>
              To be India’s most respected and reliable name in consultancy and workforce solutions,
              shaping the future of work with visionary strategy and unwavering professionalism.
            </p>
          </IconCard>

          <IconCard
            animationDelay="0.3s" // Delay for the second card
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19.44 9.03A1.5 1.5 0 0018 9H6a1.5 1.5 0 00-1.44 2.53L12 18.3l7.44-6.74a1.5 1.5 0 000-2.53zM12 15.83L6.44 11H17.55L12 15.83z"/><path d="M12 3L2 12h3v8h14v-8h3L12 3zm7 15h-2v-5H7v5H5v-6.68l7-6.36 7 6.36V18z"/></svg>
            }
            title="Our Mission"
          >
            <ul>
              <li>To empower businesses by providing top-tier, ethical, and efficient manpower solutions tailored to industry-specific needs.</li>
              <li>To deliver strategic consultancy that enables scalable growth, operational excellence, and long-term success.</li>
              <li>To foster a work culture driven by ethics, innovation, accountability, and performance.</li>
            </ul>
          </IconCard>
        </div>
      </Section>

    
    </div>
  );
};

export default AboutUs;